import AtradiusClientRow, {
  coverStatus,
} from "../components/AtradiusClientRow";
import PageHeading from "../components/PageHeading";
import SearchPending from "../components/SearchPending";
import { useDebouncedValue } from "../hooks/useDebouncedValue";
import { createListSearch } from "../utils/listSearch";
import { apiErrorMessage } from "../utils/apiError";
import { useEffect, useState } from "react";
import { Search, Loader2 } from "lucide-react";
import axios from "../axios/AxiosInstance";
import ClientDetail from "./ClientDetail";
import { loadClientCovers } from "../services/atradiusReview";

export default function AtradiusPage() {
  const [clients, setClients] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [appliedSearch, searching] = useDebouncedValue(search);
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [reload, setReload] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    setLoading(true);
    setError("");
    async function load() {
      try {
        const { data } = await axios.get("client/freelance", { signal: controller.signal });
        const list = Array.isArray(data) ? data : [];
        if (active) setClients(list);
        const covers = await loadClientCovers(list);
        if (active)
          setRows(
            list
              .filter((client) => covers[client._id])
              .map((client) => ({ client, cover: covers[client._id] })),
          );
      } catch (error) {
        const detail = apiErrorMessage(error, "Impossible de charger les couvertures Atradius.");
        if (active && detail) {
          setRows([]);
          setError(detail);
        }
      } finally {
        if (active && !controller.signal.aborted) setLoading(false);
      }
    }
    load();
    return () => {
      active = false;
      controller.abort();
    };
  }, [reload]);
  const filtered = rows
    .filter(
      createListSearch(appliedSearch, [
        "client.companyName",
        "client.denomination",
        "client.siret",
        "client.town",
        "client.contactPhone",
        "client.contactEMail",
        "cover.coverType",
        "cover._doc.coverType",
        "cover.status",
        "cover.requestDate",
        "cover._doc.requestDate",
      ]),
    )
    .filter(
      (row) =>
        statusFilter === "all" || coverStatus(row.cover) === statusFilter,
    );
  return (
    <div className="">
      <header className="mb-6 flex items-center gap-3">
        <div>
          <PageHeading
            icon="atradius"
            title={<> Atradius </>}
            description={
              <>
                {" "}
                Suivez les demandes de couverture et les montants accordés à vos
                clients.{" "}
              </>
            }
          />
        </div>
      </header>
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div className="relative w-full max-w-md">
          <Search
            size={18}
            aria-hidden="true"
            className="absolute left-3 top-3 text-slate-400"
          />
          <input
            aria-label="Rechercher une couverture par client ou SIRET"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher un client ou un SIRET…"
            className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-10 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <div className="mt-2"><SearchPending pending={searching} /></div>
        </div>
        <select
          aria-label="Filtrer par état Atradius"
          value={statusFilter}
          onChange={(event) => setStatusFilter(event.target.value)}
          className="rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700"
        >
          <option value="all">Tous les états</option>
          {["OK", "En attente", "Refusée", "À revoir"].map((status) => (
            <option key={status} value={status}>
              {status}
            </option>
          ))}
        </select>
        {statusFilter !== "all" && (
          <button
            type="button"
            onClick={() => setStatusFilter("all")}
            className="text-sm text-blue-700 hover:underline"
          >
            Réinitialiser
          </button>
        )}
        <button
          disabled={loading}
          onClick={() => setReload((value) => value + 1)}
          className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm text-slate-600 hover:bg-blue-50 disabled:opacity-50"
        >
          {loading && <Loader2 size={16} className="animate-spin" aria-hidden="true" />}
          Actualiser
        </button>
      </div>
      {error && (
        <p
          role="alert"
          className="mb-4 rounded-xl bg-amber-50 p-4 text-sm text-amber-800"
        >
          {error}
        </p>
      )}
      {loading ? (
        <div
          role="status"
          className="flex justify-center gap-3 py-16 text-slate-500"
        >
          <Loader2 className="animate-spin" />
          Chargement des couvertures…
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <table className="min-w-full text-left text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                {[
                  "Client",
                  "Date demande",
                  "Type",
                  "Demandé",
                  "Accordé",
                  "CA mois HT",
                  "État",
                  "Action",
                ].map((label) => (
                  <th
                    key={label}
                    scope="col"
                    className="whitespace-nowrap px-4 py-4 font-semibold"
                  >
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map(({ client, cover }) => (
                <AtradiusClientRow
                  key={client._id}
                  client={client}
                  cover={cover}
                  onOpen={setSelected}
                />
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td
                    colSpan={8}
                    className="px-4 py-12 text-center text-slate-500"
                  >
                    {search || statusFilter !== "all"
                      ? "Aucune couverture ne correspond à vos filtres."
                      : "Aucune couverture Atradius disponible."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      {selected && (
        <ClientDetail
          client={selected}
          setClient={setSelected}
          clients={clients}
          setClients={setClients}
          originalClients={clients}
          initialSection="financial"
        />
      )}
    </div>
  );
}
